import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { connect } from 'react-redux'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import {
  makeStyles,
  createStyles,
  Typography,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  Fade,
  Grow,
} from '@material-ui/core'
import Button from '../Button/Button'
import { downloadURI, isPurchasedItem, numberToPrice } from '../../utils/utils'
import ProductSatisfactionForm from '../Order/ProductSatisfactionForm'
import GlobalSatisfactionForm from '../Order/GlobalSatisfactionForm'
import OrderProductRow from '../Order/OrderProductRow'
import SubscriptionListHeader from './SubscriptionListHeader'
import { postReviews } from '../../redux/actions/reviews'
import { getStoresByOrderId } from '../../redux/actions/inStore'
import CMSText from '../Global/CMSText'
import InStoreReturnLink from '../InStore/InStoreLink'
import ReturnVoucherButton from '../ReturnVoucherButton/ReturnVoucherButton'
import { Contract, Order } from '../../interfaces/common/contract'
import { RootState } from '../../redux/reducers/rootReducer'
const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)

const useStyles = makeStyles((theme) =>
  createStyles({
    orderCard: {
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(3),
      marginTop: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
      },
    },
    card: {
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(3),
    },
    orderCardInStore: {
      marginTop: theme.spacing(1),
    },
    orderList: {
      marginTop: theme.spacing(2),
    },
    validModif: {
      width: '100%',
      textAlign: 'center',
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1),
      position: 'sticky',
      bottom: 0,
      background: theme.palette.common.white,
    },
    validModifInStore: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing(1),
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1),
      background: theme.palette.common.white,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    inStoreRow: {
      textAlign: 'center',
    },
    subscription: {
      [theme.breakpoints.down('md')]: {
        fontSize: 13,
      },
    },
    price: {
      [theme.breakpoints.down('md')]: {
        fontSize: 13,
      },
    },
    separatorProduct: {
      borderBottom: `1px solid ${theme.extraColors.greyLight}`,
      margin: theme.spacing(1),
    },
    spacing: {
      marginLeft: 19,
    },
    warehouseContainer: {
      margin: theme.spacing(2, 0),
    },
    inProgress: {
      overflow: 'hidden',
      '& >span': {
        position: 'relative',
        color: theme.palette.common.white,
        zIndex: 2,
      },
      '&:before': {
        content: '""',
        zIndex: 1,
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: theme.palette.secondary.main,
        animation: '$progress 5s forwards',
        transformOrigin: 'left',
        willChange: 'transform',
      },
    },
    '@keyframes progress': {
      '0%': {
        transform: 'scale3d(0, 1, 1)',
      },
      '20%': {
        transform: 'scale3d(0.8, 1, 1)',
      },
      '80%': {
        transform: 'scale3d(0.9, 1, 1)',
      },
      '100%': {
        transform: 'scale3d(1.1, 1, 1)',
      },
    },
    buyArea: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  })
)

type Tprops = ReturnType<typeof mapStateToProps> & {
  doc?: any
  orders: Order[]
  postProductToReturn: (data: object) => any
  customer_survey: any
  t: any
  prismicAccount?: any
  postReviews: (data: object) => any
  getStoresByOrderId: (any) => void
  isFunnel?: boolean
  saveProductToReturn?: (data: object) => any
  submitConnectedData?: () => any
  contract: Contract
  settings: any
  inStoreText: any
}

const mapStateToProps = (state: RootState) => ({
  promotions: state.promotions,
  user: state.user,
  parameters: state.parameters,
})

const mapDispatchToProps = {
  postReviews: postReviews,
  getStoresByOrderId: getStoresByOrderId,
}

const SubscriptionList = ({
  orders,
  postProductToReturn,
  saveProductToReturn,
  submitConnectedData,
  promotions,
  isFunnel,
  postReviews,
  contract,
  getStoresByOrderId,
  user,
  parameters,
  t,
  prismicAccount,
  doc,
  customer_survey,
  settings,
  inStoreText,
}: Tprops) => {
  const classes = useStyles()
  const router = useRouter()
  const theme = useTheme()
  const [totalCurrent, setTotalCurrent] = useState(0)
  const [isModifying, setIsModifying] = useState(isFunnel ? true : false)
  const [productToReturn, setProductToReturn] = useState([])
  const [newTotal, setNewTotal] = useState(totalCurrent)
  const [returnAll, setReturnAll] = useState(false)
  const [currentSubscriptionPrice, setCurrentSubscriptionPrice] = useState(0)
  const [totalAnswers, setTotalAnswers] = useState([])
  const [globalReview, setGlobalReview] = useState({})
  const [returnedProducts, setReturnedProducts] = useState([])
  const [boughtProducts, setBoughtProducts] = useState([])
  const [filteredOrdersCancelled, setFilteredOrdersCancelled] = useState([])
  const [filteredOrdersRefunded, setFilteredOrdersRefunded] = useState([])
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [currentSubscriptionMaxItems, setCurrentSubscriptionMaxItems] =
    useState(0)
  const [newSubscriptionPrice, setNewSubscriptionPrice] = useState(0)
  const [newSubscriptionMaxItems, setNewSubscriptionMaxItems] = useState(0)
  const [allProducts, setAllProducts] = useState([])
  const [
    filteredOrdersAndProductsByWarehouse,
    setFilteredOrdersAndProductsByWarehouse,
  ] = useState([])
  const [productSelection, setProductSelection] = useState(false)
  const [downloadLink, setDownloadLink] = useState('')
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isClassicSub = process.env.STORE_TYPE === 'classic_subscription'
  const [warehouses, setWarehouses] = useState([])
  const isBuyingOption = process.env.BUYING_OPTION
  const { trackEvent } = useMatomo()
  const isDisplayCustomerSurvey = customer_survey?.data?.display_customer_survey

  useEffect(() => {
    if (parameters?.items?.in_store) {
      const isAsync = async () => {
        const orderNumbers = []
        orders?.length > 0 &&
          orders?.map((order) => {
            order.state !== 'cart' &&
              order.state !== 'cancelled' &&
              orderNumbers.push(order.number)
          })
        const response: any = await getStoresByOrderId(orderNumbers)
        if (response?.type === 'GET_STORES_BY_ORDER_NUMBER_SUCCESS') {
          setWarehouses(response?.stores)
        }
      }
      isAsync()
    }
  }, [orders, parameters])

  // Select all product from
  const handleReturnAll = () => {
    const productsArray = []
    if (returnAll === false || productToReturn.length === 0) {
      setReturnAll(true)
      setProductSelection(true)
      allProducts.map((item, index) => {
        if (dayjs().isAfter(item?.units[0]?.startLeasingDate)) {
          productsArray.push({
            id: index,
            code: item.variant.code,
            total: item.total,
            product: item,
            carrierId: item.carrierId,
            expand: true,
            Sku: item.variant.sku,
            quantity: item.quantity,
            orderToken: item.orderToken,
          })
        }
      })
      setNewTotal(0)
      setNewSubscriptionPrice(0)
      setNewSubscriptionMaxItems(0)
    } else {
      setReturnAll(false)
      setProductSelection(false)
      setNewTotal(totalCurrent)
      setNewSubscriptionPrice(currentSubscriptionPrice)
      setNewSubscriptionMaxItems(currentSubscriptionMaxItems)
    }
    setProductToReturn(productsArray)
  }

  // get all products in array from orders
  const extractProducts = (arrayOrder) => {
    const arrayReturned = []
    arrayOrder.map((order) => {
      order.items.length > 0 &&
        order.items?.map((product) => {
          product['orderToken'] = order.tokenValue
          arrayReturned.push(product)
        })
    })
    return arrayReturned
  }

  // when recieving orders, filter all into different array and calcul
  useEffect(() => {
    if (orders && orders.length > 0) {
      // Get actual order state
      const fullProductArray = []
      const returnedProductsArray = []
      let totalWithoutReturnedAndBoughtProduct = 0
      const boughtProductsArray = []
      const filteredOrdersCancelled = orders.filter(
        (order) => order.paymentState === 'cancelled'
      )
      // Exclude paid, cancelled and refunded orders + filter on current month
      const filteredOrders = orders.filter(
        (order) => order.state !== 'cart' && order.state !== 'cancelled'
      )
      // After filteredOrders, we send the tokens to DLP to get the warehouses. We then create a new array of filtered orders grouped by warehouses
      const filteredOrdersRefunded = orders.filter(
        (order) => order.paymentState === 'refunded'
      )

      const filteredOrdersCancelledArray = extractProducts(
        filteredOrdersCancelled
      )
      const filteredOrdersRefundedArray = extractProducts(
        filteredOrdersRefunded
      )

      // mapping orders to calcul the subscription price and to create a new array wich contain every product row in the orders
      const warehouseOrdersProducts = []
      // récupération du dernier carrierId pour l'associer à tous les orders, même ceux qui n'ont pas de shipments (comme les renouvellements)
      if (user?.orders?.length > 0) {
        user?.orders?.map((userOrder) => {
          if (userOrder.shipments.length > 0) {
            filteredOrders.map((filteredOrder) => {
              filteredOrder['shipments'] = userOrder.shipments
            })
          }
        })
        if (parameters?.items?.in_store) {
          warehouses.map((warehouse, indexWarehouse) => {
            warehouseOrdersProducts.push({
              id: warehouse.id,
              name: warehouse.name,
              address: warehouse.address,
              city: warehouse.city,
              orders: [],
            })
            warehouse.orders.map((whOrder, indexOrder) => {
              if (
                typeof filteredOrders.find((order) =>
                  order?.initialOrderNumber?.length > 0 &&
                  order?.initialOrderNumber[0]?.length > 0
                    ? order.initialOrderNumber[0] === whOrder.orderNumber
                    : order.number === whOrder.orderNumber
                ) !== 'undefined'
              ) {
                const order = filteredOrders.find((order) =>
                  order?.initialOrderNumber?.length > 0 &&
                  order?.initialOrderNumber[0]?.length > 0
                    ? order.initialOrderNumber[0] === whOrder.orderNumber
                    : order.number === whOrder.orderNumber
                )
                warehouseOrdersProducts[indexWarehouse].orders.push({
                  number:
                    order?.initialOrderNumber?.length > 0 &&
                    order?.initialOrderNumber[0]?.length > 0
                      ? order.initialOrderNumber[0]
                      : order.number,
                  startLeasingDate: order.startLeasingDate,
                  tokenValue: order.tokenValue,
                  carrierId:
                    order?.shipments?.length > 0
                      ? order?.shipments[0].method.carrierId
                      : [],
                  products: [],
                })
                order.items.length > 0 &&
                  order.items?.map((product) => {
                    product.units?.map((unit) => {
                      if (unit.isReturned) {
                        returnedProductsArray.push({
                          product: product,
                          unit: unit,
                        })
                      } else {
                        warehouseOrdersProducts[indexWarehouse].orders[
                          indexOrder
                        ].products.push({
                          ...product,
                          puid: warehouse?.orders[indexOrder]?.products?.find(
                            (whProduct) =>
                              whProduct.sku ===
                              product?.product?.variants[0]?.sku
                          )?.orderNumber,
                        })
                        fullProductArray.push(product)
                        totalWithoutReturnedAndBoughtProduct +=
                          product.unitPrice
                      }
                    })
                    product['size'] = product.variant.size
                    product['orderToken'] = order.tokenValue
                    product['orderNumber'] = order.number
                    product['carrierId'] =
                      order?.shipments?.length > 0
                        ? order?.shipments[0].method.carrierId
                        : undefined
                  })
              }
            })
          })
        } else {
          filteredOrders?.map((order) => {
            order.items.length > 0 &&
              order.items?.map((product) => {
                if (isPurchasedItem(product)) {
                  boughtProductsArray.push({ product: product })
                }
                product.units.map((unit) => {
                  if (unit.isReturned) {
                    product['quantity'] = 1
                    !isPurchasedItem(product) &&
                      returnedProductsArray.push({
                        product: product,
                        unit: unit,
                      })
                  } else {
                    if (!isPurchasedItem(product)) {
                      product['quantity'] = 1
                      fullProductArray.push(product)
                      totalWithoutReturnedAndBoughtProduct += product.unitPrice
                    }
                  }
                })
                product['size'] = product.variant.size
                product['orderToken'] = order.tokenValue
                product['orderNumber'] = order.number
                product['carrierId'] = order.shipments[0].method.carrierId
              })
          })
        }
        // Set the different Prices and the array
        setFilteredOrdersCancelled(filteredOrdersCancelledArray)
        setFilteredOrdersRefunded(filteredOrdersRefundedArray)
        setAllProducts(fullProductArray)
        setFilteredOrdersAndProductsByWarehouse(warehouseOrdersProducts)
        setReturnedProducts(returnedProductsArray)
        setBoughtProducts(boughtProductsArray)
        setTotalCurrent(numberToPrice(totalWithoutReturnedAndBoughtProduct))
        setNewTotal(numberToPrice(totalWithoutReturnedAndBoughtProduct))
        // For classic subscription we also need to compare the current subscription state to the promotion we recieve to know wich forfait is applied
        if (isClassicSub) {
          if (promotions?.items?.length > 0) {
            let userSubscriptionQuantity = 0
            let currentSubscriptionMaxItems = 0
            let currentSubscriptionPrice = 0
            // Only product not returned
            userSubscriptionQuantity = fullProductArray.length
            // Filter plans from coupon_based promotions
            const plans = promotions?.items?.filter(
              (item) => !item.coupon_based
            )
            plans?.map((plan) => {
              if (
                userSubscriptionQuantity >= plan.rules[0].configuration.from &&
                userSubscriptionQuantity <= plan.rules[0].configuration.to
              ) {
                currentSubscriptionMaxItems = plan.rules[0].configuration.to
                currentSubscriptionPrice =
                  plan.actions[0].configuration[
                    Object.keys(promotions.items[0].actions[0].configuration)[0]
                  ].amount
              }
            })
            setCurrentSubscriptionPrice(currentSubscriptionPrice)
            setCurrentSubscriptionMaxItems(currentSubscriptionMaxItems)
            setNewSubscriptionPrice(currentSubscriptionPrice)
            setNewSubscriptionMaxItems(currentSubscriptionMaxItems)
          }
        }
      }
    }
  }, [orders, warehouses, user?.orders])

  const handleProductSelection = (
    Sku,
    price,
    id,
    quantity,
    token,
    item,
    orderNumber?: string,
    warehouseId?: string,
    carrierId?: number
  ) => {
    setProductSelection(true)
    const ProductSelectionArray = [...productToReturn]
    // we first check if the product selected is already in the array of selected products
    if (productToReturn.find((product) => product.id === id)) {
      // if it is true the we remove the row in the array and we adapt the prices
      const index = productToReturn.findIndex((product) => product.id === id)
      const warehouseId = productToReturn.findIndex(
        (product) => product.warehouseId === warehouseId
      )
      ProductSelectionArray.splice(index, 1)
      if (newTotal === totalCurrent) {
        setNewTotal(totalCurrent + parseFloat(price) / 100)
      } else {
        setNewTotal(newTotal + parseFloat(price) / 100)
      }
      // if nothing selected, disable generate button
      setProductSelection(ProductSelectionArray.length !== 0)
    } else {
      // if it is false we push a new row in the array of selected products and we adapt the prices
      ProductSelectionArray.push({
        id: id,
        Sku: Sku,
        total: price,
        expand: true,
        product: item.variant,
        size: item?.variant?.size,
        quantity: quantity,
        orderToken: token,
        orderNumber: orderNumber,
        warehouseId: warehouseId,
        carrierId: carrierId,
      })
      // if nothing selected, disable generate button
      setProductSelection(ProductSelectionArray.length !== 0)
      if (newTotal === totalCurrent) {
        setNewTotal(totalCurrent - parseFloat(price) / 100)
      } else setNewTotal(newTotal - parseFloat(price) / 100)
    }
    setProductToReturn(ProductSelectionArray)
    // same in classic subscription but we also check if it will change the current forfait if the customer return the current selection
    if (isClassicSub) {
      if (promotions?.items.length > 0) {
        let userSubscriptionQuantity = 0
        let selectedProductQuantity = 0
        let currentSubscriptionPrice = 0
        let currentSubscriptionMaxItems = 0
        ProductSelectionArray.map((order) => {
          selectedProductQuantity += order.quantity
        })
        userSubscriptionQuantity = allProducts.length
        // Filter plans of coupon based promotions
        const plans = promotions?.items?.filter((item) => !item.coupon_based)
        plans?.map((plan) => {
          if (
            userSubscriptionQuantity - selectedProductQuantity >=
              plan.rules[0].configuration.from &&
            userSubscriptionQuantity - selectedProductQuantity <=
              plan.rules[0].configuration.to
          ) {
            // Because Sylius is complex (and bullshit) we have to go through a lot of objects to find the amount of the current available subscription
            currentSubscriptionPrice =
              plan.actions[0].configuration[
                Object.keys(promotions.items[0].actions[0].configuration)[0]
              ].amount
            currentSubscriptionMaxItems = plan.rules[0].configuration.to
          }
        })
        setNewSubscriptionPrice(currentSubscriptionPrice)
        setNewSubscriptionMaxItems(currentSubscriptionMaxItems)
      }
    }
  }

  const cancelReturn = () => {
    // we reset everything
    setIsModifying(false)
    setReturnAll(false)
    setProductSelection(false)
    setProductToReturn([])
    setNewTotal(totalCurrent)
    setNewSubscriptionPrice(currentSubscriptionPrice)
    setNewSubscriptionMaxItems(currentSubscriptionMaxItems)
  }

  const sendProductsToReturn = async (warehouseId?: number) => {
    // we prepare the data to send, the send it and check if response is ok, then we download the recieved file
    setIsDownloadLoading(true)
    let productData = []
    if (warehouseId && typeof warehouseId === 'number') {
      productData = productToReturn
        .filter((product) => product.warehouseId === warehouseId)
        .map((product) => ({
          lizeeSKU: product.Sku,
          quantity: product.quantity,
          orderToken: product.orderToken,
        }))
    } else {
      productData = productToReturn.map((product) => ({
        lizeeSKU: product.Sku,
        quantity: product.quantity,
        orderToken: product.orderToken,
      }))
    }

    const products = {
      products: productData,
      gender: user?.user?.gender,
      warehouseId: productToReturn[0].warehouseId,
      carrierId: productToReturn[0].carrierId,
    }
    const data = await postProductToReturn(products)
    if (data.type === 'POST_PRODUCT_TO_RETURN_SUCCESS') {
      if (totalAnswers.length > 0) {
        totalAnswers[0]['globalReview'] = globalReview
        totalAnswers.map(async (totalAnswer) => {
          totalAnswer['email'] = user.user.email
          await postReviews(totalAnswer)
        })
      }
      router.push('/confirmReturn')
      if (data?.response[0]?.pdfUrl) {
        await setDownloadLink(data?.response[0]?.pdfUrl)
        await downloadURI(
          data.response[0].pdfUrl,
          `${process.env.STORE}_${data.response[0].trackingCode}`
        )
        await setIsDownloadLoading(false)
      }
    } else {
      setIsDownloadLoading(false)
    }
  }

  const funnelSaveProductsToReturn = async () => {
    await setIsDownloadLoading(true)
    // Save products in redux state to continue through the funnel
    await saveProductToReturn(productToReturn)
    await submitConnectedData()
    await setIsDownloadLoading(false)
  }

  const saveAnswers = (answers, sku, orderId) => {
    const totalAnswersArray = [...totalAnswers]
    let currentOrder = totalAnswersArray.find(
      (item) => item.orderId === orderId
    )
    if (!currentOrder) {
      totalAnswersArray.push({ orderId: orderId, reviews: [] })
      currentOrder = totalAnswersArray.find((item) => item.orderId === orderId)
    }
    const currentProduct = currentOrder.reviews.find((item) => item.sku === sku)
    if (!currentProduct) {
      totalAnswersArray
        .find((item) => item.orderId === orderId)
        .reviews.push({ sku: sku, answers: answers })
    } else {
      totalAnswersArray
        .find((item) => item.orderId === orderId)
        .reviews.find((item) => item.sku === sku).answers = answers
    }
    setTotalAnswers(totalAnswersArray)
  }

  const handleGlobalReview = (notes) => {
    //const globalReviewState = globalReview
    globalReview['NPS'] = notes[0]?.note ? notes[0]?.note : null
    globalReview['expeditionRate'] = notes[1]?.note ? notes[1]?.note : null
    globalReview['returnRate'] = notes[2]?.note ? notes[2]?.note : null
    setGlobalReview(globalReview)
  }

  const handleMessage = (message) => {
    globalReview['renterReview'] = message
    setGlobalReview(globalReview)
  }

  const InStoreReturnBtn = () => (
    <InStoreReturnLink
      isInStore={parameters?.items?.in_store}
      isBlank
      cmsDefaultText={t('texts:instore:account_page_return_link_label')}
      cmsText={inStoreText?.data?.account_page_return_link_label}
    />
  )

  return (
    <>
      {allProducts?.length > 0 && (
        <div
          className={clsx(
            'orders--current',
            !parameters?.items?.in_store
              ? classes.orderCard
              : classes.orderCardInStore
          )}
        >
          <SubscriptionListHeader
            isFunnel={isFunnel}
            allProducts={allProducts}
            isClassicSub={isClassicSub}
            isSmallScreen={isSmallScreen}
            isModifying={isModifying}
            doc={doc}
            t={t}
            settings={settings}
            isInStore={parameters?.items?.in_store}
            cancelReturn={cancelReturn}
            setIsModifying={setIsModifying}
            contract={contract}
            orders={user?.orders}
            totalCurrent={totalCurrent}
            newTotal={newTotal}
            currentSubscriptionPrice={currentSubscriptionPrice}
            currentSubscriptionMaxItems={currentSubscriptionMaxItems}
            newSubscriptionPrice={newSubscriptionPrice}
            newSubscriptionMaxItems={newSubscriptionMaxItems}
          />
          {isModifying && !parameters?.items?.in_store && (
            <div className={classes.orderList}>
              <Fade in={isModifying}>
                <div className={classes.spacing}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-testid={'return_all'}
                        checked={returnAll && productToReturn.length !== 0}
                        onChange={() => handleReturnAll()}
                        name={'return_all'}
                      />
                    }
                    label={
                      <Typography variant={'body2'}>
                        <CMSText
                          asText
                          data={doc?.data?.select_all_and_stop}
                          defaultText={t(
                            'texts:subscription_order:select_all_and_stop'
                          )}
                        />
                      </Typography>
                    }
                  />
                </div>
              </Fade>
            </div>
          )}
          {/* MAP on each warehouse and show each products */}
          {!parameters?.items?.in_store && (
            <>
              {allProducts.map((item, index) => {
                let isExpanded = false
                const isSelected = productToReturn.find(
                  (product) => product.id === index
                )
                  ? true
                  : false
                if (isSelected) {
                  isExpanded = productToReturn.find(
                    (product) => product.id === index
                  ).expand
                }
                return (
                  <span key={index}>
                    <OrderProductRow
                      isModifying={isModifying}
                      isSelected={isSelected}
                      index={index}
                      carrierId={item.carrierId}
                      orderToken={item.orderToken}
                      t={t}
                      showQuantity={false}
                      item={item}
                      orderNumber={item.orderNumber}
                      handleProductSelection={handleProductSelection}
                      showPrice={!settings?.only_add_with_pack}
                      showSizes={settings?.show_size_name}
                      settings={settings}
                      doc={doc}
                    />
                    {isDisplayCustomerSurvey && (
                      <Grow in={isExpanded} mountOnEnter unmountOnExit>
                        <ProductSatisfactionForm
                          t={t}
                          doc={customer_survey}
                          orderId={item.orderNumber}
                          saveAnswers={saveAnswers}
                          sku={item.variant.sku}
                          answersSaved={totalAnswers
                            .find(
                              (answer) => answer.orderId === item.orderNumber
                            )
                            ?.reviews?.find(
                              (answer) => answer.sku === item.variant.sku
                            )}
                        />
                      </Grow>
                    )}
                    <div
                      className={clsx(
                        'orders--current__buy',
                        isBuyingOption === 'true' && classes.buyArea
                      )}
                    >
                      {isBuyingOption === 'true' && (
                        <>
                          <Button
                            variant={'contained'}
                            color={'secondary'}
                            size={'small'}
                            onClick={() =>
                              trackEvent({
                                category: 'Buying option click',
                                action: `User clicked on the buying option button`,
                              })
                            }
                          >
                            {t('texts:account:buy')}
                          </Button>
                        </>
                      )}
                    </div>
                    <div className={classes.separatorProduct}></div>
                  </span>
                )
              })}
              {isModifying && isDisplayCustomerSurvey && (
                <GlobalSatisfactionForm
                  doc={customer_survey}
                  t={t}
                  setGlobalReview={handleGlobalReview}
                  handleMessage={handleMessage}
                />
              )}
              {isModifying && (
                <>
                  {!isFunnel ? (
                    <div className={classes.validModif}>
                      <ReturnVoucherButton
                        downloadLink={downloadLink}
                        t={t}
                        prismicData={prismicAccount}
                        isDisabled={isDownloadLoading || !productSelection}
                        isDownloadLoading={isDownloadLoading}
                        text={
                          <CMSText
                            asText
                            data={doc?.data?.generate_the_return_slip}
                            defaultText={t(
                              'texts:subscription_order:generate_the_return_slip'
                            )}
                          />
                        }
                        onValidate={() => sendProductsToReturn()}
                      />
                    </div>
                  ) : (
                    <div className={classes.validModif}>
                      <ReturnVoucherButton
                        downloadLink={downloadLink}
                        t={t}
                        prismicData={prismicAccount}
                        isDisabled={isDownloadLoading}
                        isDownloadLoading={isDownloadLoading}
                        text={
                          <CMSText
                            asText
                            data={doc?.data?.continue}
                            defaultText={t('texts:subscription_order:continue')}
                          />
                        }
                        onValidate={() => funnelSaveProductsToReturn()}
                      />
                      <Typography variant={'body2'}>
                        <CMSText
                          asText
                          data={doc?.data?.bottom_sentence}
                          defaultText={t(
                            'texts:subscription_order:bottom_sentence'
                          )}
                        />
                      </Typography>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {/* MAP on each warehouse and show each products */}
          {parameters?.items?.in_store &&
            !isFunnel &&
            filteredOrdersAndProductsByWarehouse.map(
              (warehouse, warehouseIndex) => {
                // Check if there are non-returned product inside the orders
                let hasProducts = false
                warehouse.orders.map((order) => {
                  if (order.products.length > 0) {
                    hasProducts = true
                  }
                })
                if (hasProducts) {
                  let i = 0
                  return (
                    <div className={classes.card} key={warehouseIndex}>
                      <Typography variant={'h5'}>{warehouse.name}</Typography>
                      <Typography variant={'body2'}>
                        {warehouse.address + ' ' + warehouse.city}
                      </Typography>
                      {warehouse.orders.map((order, orderIndex) => {
                        return (
                          <div key={orderIndex}>
                            {order.products.map((item, productIndex) => {
                              i++
                              let isExpanded = false
                              const isSelected = productToReturn.find(
                                (product) =>
                                  product.id === i &&
                                  product.warehouseId === warehouse.id
                              )
                                ? true
                                : false
                              if (isSelected) {
                                isExpanded = productToReturn.find(
                                  (product) => product.id === i
                                ).expand
                              }
                              return (
                                <span key={productIndex}>
                                  <OrderProductRow
                                    isModifying={isModifying}
                                    isSelected={isSelected}
                                    index={i}
                                    t={t}
                                    orderToken={order.tokenValue}
                                    carrierId={order.carrierId}
                                    puid={item.puid}
                                    item={item}
                                    showQuantity={false}
                                    warehouseId={warehouse.id}
                                    orderNumber={order?.number}
                                    handleProductSelection={
                                      handleProductSelection
                                    }
                                    showPrice={!settings?.only_add_with_pack}
                                    showSizes={settings?.show_size_name}
                                    settings={settings}
                                    doc={doc}
                                  />
                                  {isDisplayCustomerSurvey && (
                                    <Grow
                                      in={isExpanded}
                                      mountOnEnter
                                      unmountOnExit
                                    >
                                      <ProductSatisfactionForm
                                        t={t}
                                        doc={customer_survey}
                                        orderId={item.orderNumber}
                                        saveAnswers={saveAnswers}
                                        sku={item.variant.sku}
                                        answersSaved={totalAnswers
                                          .find(
                                            (answer) =>
                                              answer.orderId ===
                                              item.orderNumber
                                          )
                                          ?.reviews?.find(
                                            (answer) =>
                                              answer.sku === item.variant.sku
                                          )}
                                      />
                                    </Grow>
                                  )}

                                  <div
                                    className={classes.separatorProduct}
                                  ></div>
                                </span>
                              )
                            })}
                          </div>
                        )
                      })}
                      {isModifying && (
                        <>
                          {!isFunnel ? (
                            <div className={classes.validModifInStore}>
                              <ReturnVoucherButton
                                downloadLink={downloadLink}
                                t={t}
                                prismicData={prismicAccount}
                                isDisabled={
                                  isDownloadLoading ||
                                  !productSelection ||
                                  typeof productToReturn.find(
                                    (ptr) => ptr.warehouseId === warehouse.id
                                  ) === 'undefined'
                                }
                                isDownloadLoading={isDownloadLoading}
                                text={
                                  <CMSText
                                    asText
                                    data={doc?.data?.generate_the_return_slip}
                                    defaultText={t(
                                      'texts:subscription_order:generate_the_return_slip'
                                    )}
                                  />
                                }
                                onValidate={() =>
                                  sendProductsToReturn(warehouse.id)
                                }
                              />
                            </div>
                          ) : (
                            <div className={classes.validModifInStore}>
                              <ReturnVoucherButton
                                downloadLink={downloadLink}
                                t={t}
                                prismicData={prismicAccount}
                                isDisabled={isDownloadLoading}
                                isDownloadLoading={isDownloadLoading}
                                text={
                                  <CMSText
                                    asText
                                    data={doc?.data?.continue}
                                    defaultText={t(
                                      'texts:subscription_order:continue'
                                    )}
                                  />
                                }
                                onValidate={() => funnelSaveProductsToReturn()}
                              />
                              <Typography variant={'body2'}>
                                <CMSText
                                  asText
                                  data={doc?.data?.bottom_sentence}
                                  defaultText={t(
                                    'texts:subscription_order:bottom_sentence'
                                  )}
                                />
                              </Typography>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )
                }
              }
            )}
          {isModifying &&
            parameters?.items?.in_store &&
            !isFunnel &&
            isDisplayCustomerSurvey && (
              <div className={classes.validModifInStore}>
                <GlobalSatisfactionForm
                  doc={customer_survey}
                  t={t}
                  setGlobalReview={handleGlobalReview}
                  handleMessage={handleMessage}
                />
              </div>
            )}
        </div>
      )}
      {parameters.items.in_store && (
        <div className={classes.inStoreRow}>
          <InStoreReturnBtn />
        </div>
      )}
      {returnedProducts.length > 0 && !isFunnel && (
        <div className={clsx('orders--returned', classes.orderCard)}>
          <Typography variant={'h4'}>
            <CMSText
              data={doc?.data?.returned_product_title}
              defaultText={t('texts:subscription_order:returned_product_title')}
              asText
            />
          </Typography>
          <Typography variant={'body2'}>
            <CMSText
              data={doc?.data?.returned_product_desc}
              defaultText={t('texts:subscription_order:returned_product_desc')}
              asText
            />
          </Typography>
          {returnedProducts.map((item, index) => {
            return (
              <span key={index}>
                <OrderProductRow
                  isReturned={true}
                  isModifying={false}
                  isSelected={false}
                  index={index}
                  t={t}
                  showQuantity={false}
                  item={item.product}
                  unit={item.unit}
                  showPrice={true}
                  handleProductSelection={handleProductSelection}
                  showSizes={settings?.show_size_name}
                  settings={settings}
                  doc={doc}
                />
                <div className={classes.separatorProduct}></div>
              </span>
            )
          })}
        </div>
      )}
      {boughtProducts.length > 0 && !isFunnel && (
        <div className={clsx('orders--returned', classes.orderCard)}>
          <Typography variant={'h4'}>
            <CMSText
              data={doc?.data?.bought_product_title}
              defaultText={t('texts:subscription_order:bought_product_title')}
              asText
            />
          </Typography>
          <Typography variant={'body2'}>
            <CMSText
              data={doc?.data?.bought_product_desc}
              defaultText={t('texts:subscription_order:bought_product_desc')}
              asText
            />
          </Typography>
          {boughtProducts.map((item, index) => {
            return (
              <span key={index}>
                <OrderProductRow
                  isReturned={true}
                  isModifying={false}
                  isSelected={false}
                  index={index}
                  t={t}
                  isBought={true}
                  item={item.product}
                  showPrice={true}
                  unit={item.unit}
                  handleProductSelection={handleProductSelection}
                  showSizes={settings?.show_size_name}
                  settings={settings}
                  doc={doc}
                />
                <div className={classes.separatorProduct}></div>
              </span>
            )
          })}
        </div>
      )}
      {filteredOrdersCancelled.length > 0 && !isFunnel && (
        <div className={clsx('orders--cancelled', classes.orderCard)}>
          <Typography variant={'h4'}>
            <CMSText
              asText
              data={doc?.data?.canceled_order_title}
              defaultText={t('texts:subscription_order:canceled_order_title')}
            />
          </Typography>
          <Typography variant={'body2'}>
            <CMSText
              asText
              data={doc?.data?.canceled_order_desc}
              defaultText={t('texts:subscription_order:canceled_order_desc')}
            />
          </Typography>
          {filteredOrdersCancelled.map((item, index) => {
            return (
              <span key={index}>
                <OrderProductRow
                  isReturned={true}
                  isModifying={false}
                  isSelected={false}
                  index={index}
                  t={t}
                  item={item}
                  handleProductSelection={handleProductSelection}
                  showSizes={settings?.show_size_name}
                  settings={settings}
                  doc={doc}
                />
                <div className={classes.separatorProduct}></div>
              </span>
            )
          })}
        </div>
      )}
      {filteredOrdersRefunded.length > 0 && !isFunnel && (
        <div className={clsx('order--refunded', classes.orderCard)}>
          <Typography variant={'h4'}>
            <CMSText
              asText
              data={doc?.data?.refunded_order_title}
              defaultText={t('texts:subscription_order:refunded_order_title')}
            />
          </Typography>
          <Typography variant={'body2'}>
            <CMSText
              asText
              data={doc?.data?.canceled_order_desc}
              defaultText={t('texts:subscription_order:canceled_order_desc')}
            />
          </Typography>
          {filteredOrdersRefunded.map((item, index) => {
            return (
              <span key={index}>
                <OrderProductRow
                  isReturned={true}
                  isModifying={false}
                  isSelected={false}
                  index={index}
                  t={t}
                  item={item}
                  handleProductSelection={handleProductSelection}
                  showSizes={settings?.show_size_name}
                  settings={settings}
                  doc={doc}
                />
                <div className={classes.separatorProduct}></div>
              </span>
            )
          })}
        </div>
      )}
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionList)
